import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { WorkpackStatus } from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';

/**
 * Default labels for Workpack status values.
 * These are used when no custom labels are configured at the structure level.
 */
const DEFAULT_WORKPACK_STATUS_LABELS: Record<string, string> = {
  [WorkpackStatus.Inprogress]: 'In Progress',
  [WorkpackStatus.Completed]: 'Completed',
  [WorkpackStatus.Inreview]: 'In Review',
  [WorkpackStatus.ChaseItemAdded]: 'Chase Item Added',
  [WorkpackStatus.DesktopReviewInProgress]: 'Desktop Review In Progress',
  [WorkpackStatus.DesktopReviewComplete]: 'Desktop Review Complete',
  [WorkpackStatus.FieldVerificationInProgress]: 'Field Verification In Progress',
  [WorkpackStatus.FieldVerificationComplete]: 'Field Verification Complete',
  [WorkpackStatus.FinalReviewInProgress]: 'Final Review In Progress',
  [WorkpackStatus.FinalReviewComplete]: 'Final Review Complete',
};

/**
 * Hook to get dynamic Workpack status labels from Settings collection.
 * Falls back to default labels if no custom labels are configured.
 *
 * @returns Object with:
 * - `getWorkpackStatusLabel`: Function to get the display label for a given status
 * - `labels`: Record mapping each WorkpackStatus to its display label
 * - `statusOptions`: Array of { value, label } for use in dropdowns
 */
export const useWorkpackStatusLabels = () => {
  const workpackStatusLabels = useAtomValue(state.workpackStatusLabels);

  const labels = useMemo(() => {
    const result: Record<string, string> = {};

    // First, add all enum-based default labels
    Object.entries(DEFAULT_WORKPACK_STATUS_LABELS).forEach(([key, value]) => {
      result[key] = value;
    });

    // Then, override with custom labels from Settings (and add any new custom statuses)
    if (workpackStatusLabels?.meta) {
      for (const metaItem of workpackStatusLabels.meta) {
        if (metaItem.__typename === 'StringMeta') {
          result[metaItem.key] = metaItem.value;
        }
      }
    }

    return result;
  }, [workpackStatusLabels]);

  const getWorkpackStatusLabel = useMemo(() => {
    return (status: string): string => {
      return labels[status] ?? status;
    };
  }, [labels]);

  const statusOptions = useMemo(() => {
    // Return all status options from labels (both enum-based and custom from DB)
    return Object.entries(labels).map(([key, value]) => ({
      value: key,
      label: value,
    }));
  }, [labels]);

  return {
    getWorkpackStatusLabel,
    labels,
    statusOptions,
  };
};

export { DEFAULT_WORKPACK_STATUS_LABELS };
